<template>
  <div id="InquiryForm" class="wrap">
    <div class="formWrap">
      <h1 class="title">問合せ</h1>

      <p v-if="!canPost" class="periodNote">現在、問合せができる期間ではありません。内容の確認のみ可能です。</p>

      <div class="inquiryArea mx-auto p-4">
        <b-row class="mb-3">
          <div class="label bold"><span class="mr-2 badge badge-required">必須</span>氏名</div>
          <div class="mainArea">
            <b-form-input v-model="form.name" maxlength="200"/>
          </div>
        </b-row>

        <b-row class="mb-3">
          <div class="label bold"><span class="mr-2 badge badge-required">必須</span>メールアドレス</div>
          <div class="mainArea">
            <b-form-input v-model="form.mail" type="email" maxlength="256"/>
          </div>
        </b-row>

        <b-row class="mb-3">
          <div class="label bold"><span class="mr-2 badge badge-required">必須</span>タイトル</div>
          <div class="mainArea">
            <b-form-input v-model="form.title" maxlength="200" placeholder="入力してください。"/>
          </div>
        </b-row>

        <b-row class="mb-3">
          <div class="label bold"><span class="mr-2 badge badge-required">必須</span>本文</div>
          <div class="mainArea">
            <b-form-textarea v-model="form.body" :rows="8" maxlength="10000" placeholder="入力してください。"/>
          </div>
        </b-row>

        <b-row>
          <div class="label bold">添付ファイル</div>
          <div class="mainArea">
            <p v-for="(f, i) in fileList" :key="i" class="mb-1">
              {{ f.name }}
              <img src="@/assets/svg/close_small.svg" class="tdDeleteBtn" @click="removeFile(i)">
            </p>
            <div
              @dragover.prevent="toggleDrag(true)"
              @dragleave.prevent="toggleDrag(false)"
              @drop.prevent="onDrop($event)"
              :class="[isDragging ? 'is-dragging' : '', 'drop', 'flex', 'flexMiddle', 'flexCenter']">
              <span>
                <span class="dragText fw-6">ここにファイルをドラッグ＆ドロップ または</span>
                <label class="fileText fw-6 selectFile">［ ファイルを選択 ］
                  <input type="file" style="display:none;" @change="addFile">
                </label>
              </span>
            </div>
          </div>
        </b-row>
      </div>
    </div>

    <div class="btnWrap">
      <b-button class="btn back bold mr-3" @click="goList">一覧に戻る</b-button>
      <b-button variant="primary" class="btn btn-primary bold" :disabled="sending || !canPost" @click="send">送信</b-button>
    </div>
  </div>
</template>

<script>
import api from '@/modules/api';
import inquiryContext from '@/mixin/inquiryContext';

export default {
  name: 'gasInquiryForm',
  mixins: [inquiryContext],
  data() {
    return {
      form: {
        title: '',
        name: '',
        mail: '',
        body: '',
      },
      fileList: [],
      isDragging: false,
      sending: false,
      canPost: true, // 送信可否（申請期間中の申請者／採択後〜報告期間終了までの採択者のみ true）
    };
  },
  async created() {
    // 氏名・メールを自動入力（編集可）。メールはログインID（＝メール）、氏名はユーザー情報から。
    this.form.mail = this.$store.state.auth.login_id || '';
    try {
      if (this.isAgentPage) {
        // No.9 (B)：代行者は申請者向けAPIを叩けないため、代行者ページの情報（自分の氏名）を使う
        const res = await api.send('/api/agent/context');
        this.form.name = (res.data && res.data.agentName) || '';
      } else {
        const res = await api.send('/api/user/myInfo');
        const info = (res.data && res.data.userInfo) || {};
        this.form.name = `${info.sei || ''} ${info.mei || ''}`.trim();
      }
    } catch (e) {
      // 取得失敗時は空のまま（手入力可）
    }
    // 送信可否を取得（フォームは常に表示・送信のみ制御）
    try {
      const res = await api.send(`${this.inquiryApiBase}/sendable`);
      this.canPost = !!(res.data && res.data.canSend);
    } catch (e) {
      this.canPost = false;
    }
  },
  methods: {
    addFile(event) {
      if (event.target.files.length === 0) {
        return;
      }
      const input = event.target;
      this.fileList.push(input.files[0]);
      input.value = '';
    },
    toggleDrag(isDragging) {
      this.isDragging = isDragging;
    },
    onDrop(event) {
      this.isDragging = false;
      const { files } = event.dataTransfer;
      if (!files || files.length === 0) {
        return;
      }
      Object.keys(files).forEach((key) => {
        this.fileList.push(files[key]);
      });
    },
    removeFile(index) {
      this.fileList.splice(index, 1);
    },
    goList() {
      this.$router.push({ name: this.routeNames.list });
    },
    async send() {
      if (this.sending || !this.canPost) {
        return;
      }
      // 未入力チェック（バックエンド Gas/Inquiry@create の required 4項目と一致させる）
      const missing = [];
      if (!this.form.name) missing.push('氏名');
      if (!this.form.mail) missing.push('メールアドレス');
      if (!this.form.title) missing.push('タイトル');
      if (!this.form.body) missing.push('本文');
      if (missing.length > 0) {
        this.$bvModal.msgBoxOk(`${missing.join('、')}を入力してください。`);
        return;
      }
      // 形式チェック（代行者登録 MyAgent.vue と同一。Laravel の email より厳しいため
      // ここを通った値がサーバで弾かれることはない）
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(this.form.mail)) {
        this.$bvModal.msgBoxOk('メールアドレスの形式が正しくありません。');
        return;
      }
      if (!(await this.confirm('現在の内容で問合せしてよろしいですか？'))) {
        return;
      }
      this.sending = true;
      const params = {
        title: this.form.title,
        applicant_name: this.form.name,
        applicant_mail: this.form.mail,
        body: this.form.body,
        fileCnt: this.fileList.length,
      };
      this.fileList.forEach((file, i) => {
        params[`file_${i}`] = file;
      });
      try {
        await api.sendForm(`${this.inquiryApiBase}/create`, params);
        this.$router.push({ name: this.routeNames.list });
      } catch (e) {
        this.$bvModal.msgBoxOk('送信に失敗しました。');
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
  .title {
    color: #333333;
    font-weight: 300;
    font-size: 24px;
    margin: 52px 100px 0;
    border-bottom: solid 2px #A0A9D0;
    padding-left: 10px;
    padding-bottom: 7px;
  }
  .periodNote {
    width: 880px;
    margin: 20px auto 0;
    color: #C0392B;
    font-weight: 600;
    text-align: center;
  }
  .inquiryArea {
    width: 880px;
    background-color: #fff;
    margin-top: 40px;
  }
  .label {
    width: 180px;
  }
  .mainArea {
    width: 640px;
  }
  .drop {
    height: 100px;
    width: 100%;
    background-color: #F3F3F3;
    border-color: #CCC;
    border-style: dashed;
    border-radius: 10px;
    border-width: 1px;
  }
  .is-dragging.drop {
    background-color: #E6E6E6;
    border-color: #6E6E6E;
  }
  .dragText {
    color: #BBB;
  }
  .fileText {
    color: #0A8EA7;
    cursor: pointer;
  }
  .selectFile:hover {
    text-decoration: underline;
  }
  .btnWrap {
    margin: 45px 0;
    text-align: center;
  }
</style>
